<template>
  <div class="premium" role="alert">
    <div class="premium__wrapper">
      <button
        @click="$emit('toggle-premium-upgrade-modal')"
        class="premium__close"
      >
        <CloseIcon class="premium__close--icon" />
      </button>
      <div class="premium__title-wrapper">
        <h5 class="premium__title">Upgrade to Premium</h5>
      </div>
      <div v-if="isLoading" class="premium__loader-wrapper">
        <ActionLoader name="line-scale" color="#ef7d22" />
      </div>
      <div v-else class="premium__content-wrapper">
        <div class="premium__plan">
          <div class="premium__attributes-wrapper">
            <p class="premium__name">{{ plan.name }}</p>
            <p class="premium__price">
              {{ plan.price }}
              <span class="premium__term"> / per {{ plan.term }}</span>
            </p>
          </div>
          <div class="premium__details-wrapper">
            <p
              v-for="detail in details"
              :key="`detail-${detail.name}`"
              class="premium__detail"
            >
              <span class="premium__detail--checked"></span>
              {{ detail.name }}
            </p>
          </div>
        </div>
        <div class="premium__calculations-wrapper">
          <p class="premium__costs">New Calculated Costs</p>
          <div class="premium__calculations">
            <p
              v-if="comparison.discountCents !== '$0.00'"
              class="premium__calculation premium__calculation--discount"
            >
              Promotional Discount: {{ comparison.discountCents }}
            </p>
            <p class="premium__calculation">
              New Price: {{ comparison.newPrice }}
            </p>
            <p class="premium__calculation premium__calculation--due">
              <span class="premium__calculation--icon-wrapper"
                ><CartIcon class="premium__calculation--icon"
              /></span>
              Due Today: {{ comparison.dueToday }}
            </p>
            <p class="premium__calculation">
              Credit available for renewal: {{ comparison.creditForRenewal }}
            </p>
            <p class="premium__calculation">
              Next Renewal: {{ comparison.renewalDate }} for
              {{ comparison.renewalTotal }}
            </p>
            <p class="premium__calculation premium__tax-warning">
              * Tax is not calculated here but will be added upon purchase.
            </p>
          </div>
          <div class="premium__btn-wrapper">
            <button
              :disabled="isConfirmBtnDisabled"
              @click="confirmUpgrade()"
              :class="[
                'premium__confirm-btn',
                'btn__large',
                isConfirmBtnDisabled && 'premium__confirm-btn--disabled',
              ]"
            >
              confirm and upgrade
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import CloseIcon from '@/assets/icons/navigation/close_icon.svg'
import CartIcon from '@/assets/icons/subscriptions/plan_cart_icon.svg'

export default {
  name: 'PremiumUpgradeModal',
  components: {
    ActionLoader,
    CloseIcon: { ...CloseIcon, name: 'CloseIcon' },
    CartIcon: { ...CartIcon, name: 'CartIcon' },
  },
  props: {
    subscriptionId: {
      type: String,
      required: true,
    },
    couponCode: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      isLoading: true,
      isComparisonLoading: true,
      isConfirmBtnDisabled: false,
    }
  },
  computed: {
    ...mapState({
      availablePlans: state => state.subscriptions.availablePlans,
      currentSubscriptions: state => state.subscriptions.currentSubscriptions,
      details: state => state.subscriptions.planDetails,
      premiumPlanCode: state => state.subscriptions.premiumPlanCode,
    }),
    ...mapGetters({
      comparison: 'subscriptions/currentPlanComparisonBreakdown',
    }),
    plan() {
      return this.availablePlans.find(
        plan => plan.plan_code === this.premiumPlanCode
      )
    },
  },
  methods: {
    async getUpgradeData() {
      this.isLoading = true
      await this.$store.dispatch('subscriptions/fetchAvailablePlans')
      const payload = {
        id: this.subscriptionId,
        plan_code: this.plan.plan_code,
      }
      if (this.couponCode && this.couponCode.length) {
        payload.coupon_code = this.couponCode
      }
      await this.$store.dispatch('subscriptions/getPlanComparison', payload)
      this.isLoading = false
    },
    confirmUpgrade() {
      this.isConfirmBtnDisabled = true
      this.$toasted.info(
        'Please confirm that you agree to the charges and that you are ready to upgrade',
        {
          iconPack: 'material',
          position: 'bottom-center',
          icon: 'info',
          action: [
            {
              text: 'Confirm',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
                this.upgradePlan()
              },
              class: 'upgrade__toast--confirm',
            },
            {
              text: 'Cancel',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
                this.isConfirmBtnDisabled = false
              },
              class: 'upgrade__toast--cancel',
            },
          ],
        }
      )
    },
    async upgradePlan() {
      this.isLoading = true
      try {
        const { plan_code } = this.plan
        const payload = {
          id: this.subscriptionId,
          plan_code,
        }
        if (this.couponCode && this.couponCode.length) {
          payload.coupon_code = this.couponCode
        }
        await this.$store.dispatch('subscriptions/upgradePlan', payload)
        this.isLoading = false
        this.$emit('toggle-premium-upgrade-modal')
        if (this.couponCode && this.couponCode.length) {
          this.$router.push({
            name: 'user-dashboard',
            params: { mode: 'subscriptions' },
          })
        }
      } catch (error) {
        this.isLoading = false
      }
      this.isConfirmBtnDisabled = false
    },
  },
  async created() {
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    await this.getUpgradeData()
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
}
</script>

<style lang="scss">
.premium {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 402;
  background: rgba(113, 113, 113, 0.3);
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  &__wrapper {
    position: relative;
    margin: auto;
    background-image: linear-gradient(
        to bottom,
        rgba(0, 0, 0, 0.5),
        rgba(0, 0, 0, 0.5)
      ),
      url('../../assets/images/subscriptions/ccupdate.png');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: right;
    color: var(--white);
    width: 100%;
    height: 100%;
    z-index: 403;
    border-radius: 5px;
    padding: 20px;
    overflow-y: auto;
    max-width: 1600px;
    @include tablet {
      padding: 20px 50px;
      width: 75%;
      height: 95%;
    }
    @include desktop {
      padding: 20px 100px;
      width: 50%;
      height: 95%;
    }
    @include lg-desktop {
      padding: 20px 150px;
    }
  }
  &__close {
    width: 50px;
    height: 50px;
    position: absolute;
    right: 0;
    top: 0;
    background: none;
    border: none;
    cursor: pointer;
    &:hover {
      .premium__close--icon {
        fill: var(--orange);
      }
    }
    &--icon {
      fill: var(--white);
    }
  }
  &__title-wrapper {
    text-align: center;
    margin: 25px 0 0 0;
  }
  &__title {
    padding: 0;
    margin: 0 0 5px 0;
    color: var(--white);
    font-size: rem(36px);
    font-family: var(--font-bold);
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 15% 0 0 0;
  }
  &__content-wrapper {
    margin: 50px 0 0 0;
    height: 75%;
    width: 100%;
  }
  &__plan {
    background: rgba(38, 38, 38, 0.6);
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: 25px 50px;
    border-radius: 10px;
    width: 100%;
  }
  &__attributes-wrapper {
    display: flex;
    flex-direction: column;
  }
  &__name {
    font-size: rem(32px);
    margin: 0 0 15px 0;
    padding: 0;
    width: 100%;
    text-transform: capitalize;
    font-family: var(--font-bold);
  }
  &__price {
    margin: 0 0 15px 0;
    padding: 0;
    width: 100%;
    font-family: var(--font-bold);
    font-size: rem(40px);
  }
  &__term {
    text-transform: uppercase;
    color: var(--lighter-gray);
    font-size: rem(14px);
    margin: 0;
    padding: 0;
    width: 100%;
    font-family: var(--font-bold);
  }
  &__details-wrapper {
    display: flex;
    flex-direction: column;
  }
  &__detail {
    padding: 0;
    margin: 5px 0;
    display: flex;
    align-items: center;
    font-family: var(--font-bold);
    width: 100%;
    font-size: rem(14px);
    &--checked {
      margin: 0 10px 0 0;
      border: 1px solid var(--lighter-gray);
      width: 20px;
      &:before {
        display: block;
        content: '\2713';
        text-align: center;
        color: var(--green);
      }
    }
  }
  &__calculations-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin: 15px auto 0 auto;
    background: rgba(38, 38, 38, 0.6);
    padding: 25px 50px;
    border-radius: 10px;
    width: 100%;
  }
  &__costs {
    width: 100%;
    margin: 0 0 auto 0;
    padding: 0;
    font-size: rem(18px);
  }
  &__calculations {
    margin: 50px auto;
    width: 100%;
  }
  &__calculation {
    margin: 5px 0;
    padding: 0;
    color: var(--white);
    font-size: rem(18px);
    width: 100%;
    &--discount {
      color: var(--green);
    }
    &--due {
      color: var(--red);
    }
    &--icon-wrapper {
      position: absolute;
      background: var(--flat-black);
      border-radius: 100%;
      top: -2px;
      left: -37px;
      height: 30px;
      width: 30px;
      padding: 1px;
    }
    &--icon {
      padding: 7px 0 0 3.5px;
    }
  }
  &__tax-warning {
    font-size: rem(14px);
    opacity: 0.75;
    color: var(--orange);
  }
  &__btn-wrapper {
    width: 100%;
    margin: 0 auto;
  }
  &__confirm-btn {
    border-color: var(--orange);
    &--disabled {
      pointer-events: none;
    }
  }
  &__toast {
    &--confirm {
      color: var(--green) !important;
      font-family: var(--font-bold) !important;
    }
    &--cancel {
      color: var(--yellow) !important;
    }
  }
}
</style>
